import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import api, { fmt, fmtQty, fmtDate } from '../../utils/api';
import { startRegistration } from '@simplewebauthn/browser';
import PricingPage from '../Pricing/PricingPage';
import ApiKeysTab from './ApiKeysTab';
import ContainerSimulator from '../Logistics/ContainerSimulator';
export default function SettingsPage() {
  const { t, i18n } = useTranslation();
  const he = i18n.language === 'he';
  const [tab, setTab] = useState('sync');
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ username: '', password: '', fullName: '', role: 'viewer' });
  const [editingUser, setEditingUser] = useState(null);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [stockUploading, setStockUploading] = useState(false);
  const [actionPending, setActionPending] = useState(null); // generic per-row pending key
  const [stockData, setStockData] = useState([]);
  const [shagerStockData, setShagerStockData] = useState([]);
  const [stockSummary, setStockSummary] = useState([]); // /warehouse/stock-summary — true current stock incl. external warehouse
  const [extPallets, setExtPallets] = useState([]); // /external-warehouse/pallets — for editing external stock per pallet
  const [expandedExtSku, setExpandedExtSku] = useState(null); // SKU whose multi-pallet external editor is expanded
  const [stockTab, setStockTab] = useState('combined');
  const [combinedSort, setCombinedSort] = useState({ field: 'sku', dir: 'asc' });
  const [dimensions, setDimensions] = useState([]);
  const dimensionsFileRef = useRef();
  const [stockForm, setStockForm] = useState({ sku: '', description: '', quantity: '', unitCost: '', origin: 'EU', notes: '' });
  const stockFileRef = useRef();
  const shagerFileRef = useRef();

  const ROLES = ['admin', 'warehouse', 'accounting', 'brand_manager', 'account_manager', 'viewer', 'customer'];

  const fetchSync = useCallback(async () => {
    try {
      const res = await api.get('/sync/status');
      setSyncStatus(res.data);
    } catch {}
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await api.get('/auth/users');
      setUsers(res.data.users || res.data || []);
    } catch {}
  }, []);

  const fetchStock = useCallback(async () => {
    try {
      const [stRes, shagerRes, summaryRes, palletsRes] = await Promise.all([
        api.get('/warehouse/existing-stock'),
        api.get('/warehouse/received-stock'),
        api.get('/warehouse/stock-summary'),
        api.get('/external-warehouse/pallets'),
      ]);
      setStockData(stRes.data);
      setShagerStockData(shagerRes.data);
      const sum = Array.isArray(summaryRes.data) ? summaryRes.data : (summaryRes.data.items || summaryRes.data.stock || []);
      setStockSummary(sum);
      setExtPallets(palletsRes.data.pallets || palletsRes.data || []);
    } catch {}
  }, []);

  const fetchDimensions = useCallback(async () => {
    try {
      const res = await api.get('/settings/dimensions');
      setDimensions(res.data);
    } catch {}
  }, []);

  useEffect(() => {
    const fetchApprovals = async () => {
      try { const res = await api.get('/auth/pending-approvals'); setPendingApprovals(res.data || []); } catch {}
    };
    Promise.all([fetchSync(), fetchUsers(), fetchStock(), fetchDimensions(), fetchApprovals()]).finally(() => setLoading(false));
  }, [fetchSync, fetchUsers, fetchStock, fetchDimensions]);

  const uploadShagerExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const mode = window.confirm('Replace Shager stock levels?\n\nOK = Replace quantities\nCancel = Add to existing quantities') ? 'replace' : 'add';
    try {
      const XLSX = (await import('xlsx'));
      const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' });
      let updated = 0;
      for (const row of rows) {
        const sku = String(row.SKU || row.sku || row['מק"ט'] || '').trim();
        const qty = parseFloat(row.Quantity || row.quantity || row['כמות'] || 0);
        if (!sku || !qty) continue;
        const match = shagerStockData.find(s => s.sku === sku);
        if (match) {
          const newQty = mode === 'replace' ? qty : parseFloat(match.quantity) + qty;
          await api.put(`/warehouse/received-stock/${match.id}`, { quantity: newQty });
          updated++;
        }
      }
      alert(`${mode === 'replace' ? 'Replaced' : 'Added to'} ${updated} items`);
      fetchStock();
    } catch (err) { alert('Failed: ' + (err.response?.data?.error || err.message)); }
    e.target.value = '';
  };

  const addExistingStock = async (e) => {
    e.preventDefault();
    try {
      await api.post('/warehouse/existing-stock', stockForm);
      setStockForm({ sku: '', description: '', quantity: '', unitCost: '', origin: 'EU', notes: '' });
      fetchStock();
    } catch (err) { alert('Failed: ' + (err.response?.data?.error || err.message)); }
  };

  const deleteStock = async (id) => {
    if (!window.confirm('Delete this stock item?')) return;
    try { await api.delete(`/warehouse/existing-stock/${id}`); fetchStock(); } catch {}
  };

  const uploadStockExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const mode = window.confirm('Replace existing stock with this file?\n\nOK = Replace all\nCancel = Add to existing') ? 'replace' : 'add';
    try {
      const XLSX = (await import('xlsx'));
      const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' });
      const items = rows.filter(r => {
        const qty = parseFloat(r.Quantity || r.quantity || r['כמות'] || 0);
        return qty > 0;
      }).map(r => ({
        sku: String(r.SKU || r.sku || r['מק"ט'] || '').trim(),
        description: r.Product || r.product || r['מוצר'] || '',
        quantity: parseFloat(r.Quantity || r.quantity || r['כמות'] || 0),
        unitCost: r['Unit Cost'] ? parseFloat(r['Unit Cost']) : null,
        origin: r.Origin || r.origin || 'EU',
      }));
      if (!items.length) { alert('No items found'); return; }
      const res = await api.post('/warehouse/existing-stock/bulk', { items, mode });
      alert(`${mode === 'replace' ? 'Replaced' : 'Added'}: ${res.data?.added || 0} items`);
      fetchStock();
    } catch (err) { alert('Upload failed: ' + (err.response?.data?.error || err.message)); }
    e.target.value = '';
  };

  const triggerSync = async () => {
    setSyncing(true);
    try {
      await api.post('/sync/now');
      await fetchSync();
    } catch {} finally { setSyncing(false); }
  };

  const createUser = async (e) => {
    e.preventDefault();
    try {
      await api.post('/auth/users', {
        username: form.username,
        password: form.password,
        fullName: form.fullName,
        role: form.role,
      });
      setForm({ username: '', password: '', fullName: '', role: 'viewer' });
      setShowForm(false);
      fetchUsers();
    } catch {}
  };

  const updateUser = async (id, updates) => {
    try {
      await api.put(`/auth/users/${id}`, updates);
      setEditingUser(null);
      fetchUsers();
    } catch {}
  };

  const resetPassword = async (id) => {
    const pw = prompt('New password:');
    if (!pw) return;
    try {
      await api.put(`/auth/users/${id}/reset-password`, { newPassword: pw });
    } catch {}
  };

  if (loading) return <div className="loading"><div className="spinner" />{t('common.loading')}</div>;

  return (
    <div>
      <div className="sub-tabs">
        <button className={`sub-tab${tab === 'sync' ? ' active' : ''}`} onClick={() => setTab('sync')}>
          {t('sync.connected')}
        </button>
        <button className={`sub-tab${tab === 'users' ? ' active' : ''}`} onClick={() => setTab('users')}>
          {t('nav.users')}
        </button>
        <button className={`sub-tab${tab === 'stock' ? ' active' : ''}`} onClick={() => setTab('stock')}>
          {t('settings.existingStock')}
        </button>
        <button className={`sub-tab${tab === 'dimensions' ? ' active' : ''}`} onClick={() => setTab('dimensions')}>
          {he ? 'מידות מוצרים' : 'Product Dimensions'}
        </button>
        <button className={`sub-tab${tab === 'pricing' ? ' active' : ''}`} onClick={() => setTab('pricing')}>
          {t('settings.pricing')}
        </button>
        <button className={`sub-tab${tab === 'shipping' ? ' active' : ''}`} onClick={() => setTab('shipping')}>
          {he ? 'עלויות שילוח' : 'Shipping Costs'}
        </button>
        <button className={`sub-tab${tab === 'shipcalc' ? ' active' : ''}`} onClick={() => setTab('shipcalc')}>
          🧮 {he ? 'מחשבון שילוח' : 'Shipping Calculator'}
        </button>
        <button className={`sub-tab${tab === 'landed' ? ' active' : ''}`} onClick={() => setTab('landed')}>
          {he ? 'עלויות נלוות' : 'Landed Costs'}
        </button>
        <button className={`sub-tab${tab === 'expenses' ? ' active' : ''}`} onClick={() => setTab('expenses')}>
          {he ? 'הוצאות (טיפול + מחסן)' : 'Expenses'}
        </button>
        <button className={`sub-tab${tab === 'upload' ? ' active' : ''}`} onClick={() => setTab('upload')}>
          {he ? 'מרכז העלאות' : 'Upload Center'}
        </button>
        <button className={`sub-tab${tab === 'email' ? ' active' : ''}`} onClick={() => setTab('email')}>
          {he ? 'מייל ובקשות' : 'Email & Requests'}
        </button>
        <button className={`sub-tab${tab === 'apikeys' ? ' active' : ''}`} onClick={() => setTab('apikeys')}>
          🔑 API Keys
        </button>
        <button className={`sub-tab${tab === 'csim' ? ' active' : ''}`} onClick={() => setTab('csim')}>
          📦 {he ? 'סימולטור קונטיינר' : 'Container Simulator'}
        </button>
      </div>

      {/* Sync Tab */}
      {tab === 'sync' && (
        <div>
          <div className="summary-cards">
            <div className="summary-card success">
              <div className="label">{t('sync.lastSync')}</div>
              <div className="value" style={{ fontSize: 16 }}>
                {syncStatus?.lastSuccess?.synced_at ? fmtDate(syncStatus.lastSuccess.synced_at) : '--'}
              </div>
              <div className="sub">{syncStatus?.lastSuccess?.row_count || 0} rows</div>
            </div>
          </div>

          <button className="btn btn-gold mb-16" onClick={triggerSync} disabled={syncing}>
            {syncing ? t('sync.syncing') : t('sync.refresh')}
          </button>

          {/* Recent Syncs */}
          {syncStatus?.recentSyncs && syncStatus.recentSyncs.length > 0 && (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>{t('sync.time')}</th><th>{t('sync.status')}</th><th className="text-end">{t('sync.rows')}</th><th>{t('common.error')}</th></tr>
                </thead>
                <tbody>
                  {syncStatus.recentSyncs.map((s, i) => (
                    <tr key={i}>
                      <td>{s.synced_at ? new Date(s.synced_at).toLocaleString() : '--'}</td>
                      <td>
                        <span className={`badge ${s.status === 'success' ? 'badge-si' : s.status === 'no_change' ? 'badge-so' : 'badge-comp'}`}>
                          {s.status}
                        </span>
                      </td>
                      <td className="text-end">{s.row_count}</td>
                      <td className="text-danger">{s.error_message || '--'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Users Tab */}
      {tab === 'users' && (
        <div>
          <button className="btn btn-gold mb-16" onClick={() => setShowForm(!showForm)}>
            {showForm ? t('common.cancel') : '+ ' + t('settings.addUser')}
          </button>

          {showForm && (
            <form className="card mb-16" onSubmit={createUser}>
              <div className="form-row">
                <div className="form-group">
                  <label>{t('auth.username')}</label>
                  <input className="input input-sm" value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} required />
                </div>
                <div className="form-group">
                  <label>{t('auth.password')}</label>
                  <input className="input input-sm" type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required />
                </div>
                <div className="form-group">
                  <label>{t('settings.fullName')}</label>
                  <input className="input input-sm" value={form.fullName} onChange={e => setForm(f => ({ ...f, fullName: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>{t('settings.role')}</label>
                  <select className="input input-sm" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                    {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <button className="btn btn-gold btn-sm" type="submit">{t('common.save')}</button>
              </div>
            </form>
          )}

          {/* Biometric Registration */}
          <div className="card" style={{ padding: 16, marginBottom: 16, display: 'flex', gap: 16, alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <h3 style={{ fontSize: 14, marginBottom: 4 }}>🔐 Biometric Login</h3>
              <p className="text-muted" style={{ fontSize: 12 }}>Register Face ID / Touch ID / Fingerprint for this device</p>
            </div>
            <button className="btn btn-gold" onClick={async () => {
              try {
                const optionsRes = await api.post('/auth/webauthn/register-options');
                const credential = await startRegistration({ optionsJSON: optionsRes.data });
                await api.post('/auth/webauthn/register-verify', { credential, deviceName: navigator.userAgent.slice(0, 50) });
                alert('Biometric registered successfully!');
              } catch (err) {
                alert('Failed: ' + (err.response?.data?.error || err.message || 'Biometric not supported'));
              }
            }}>Register Biometric</button>
          </div>

          {/* Pending Approvals */}
          {pendingApprovals.length > 0 && (
            <div className="card" style={{ padding: 16, marginBottom: 16, border: '2px solid #e65100' }}>
              <h3 style={{ color: '#e65100', marginBottom: 12 }}>⏳ Pending Approvals ({pendingApprovals.length})</h3>
              {pendingApprovals.map(a => (
                <div key={a.id} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ flex: 1 }}>
                    <strong>{a.full_name || a.username}</strong>
                    <span className="text-muted" style={{ marginInlineStart: 8 }}>({a.role})</span>
                    <div className="text-muted" style={{ fontSize: 11 }}>{a.device_name} · {new Date(a.created_at).toLocaleString()}</div>
                  </div>
                  <button className="btn btn-sm" disabled={actionPending === `approve:${a.id}` || actionPending === `reject:${a.id}`} style={{ background: '#2e7d32', color: '#fff', opacity: (actionPending === `approve:${a.id}` || actionPending === `reject:${a.id}`) ? 0.5 : 1 }}
                    onClick={async () => { setActionPending(`approve:${a.id}`); try { await api.put(`/auth/approve/${a.id}`); const res = await api.get('/auth/pending-approvals'); setPendingApprovals(res.data || []); } catch (err) { alert((he ? 'נכשל: ' : 'Failed: ') + (err.response?.data?.error || err.message)); } finally { setActionPending(null); } }}>
                    ✓ Approve
                  </button>
                  <button className="btn btn-sm btn-danger" disabled={actionPending === `approve:${a.id}` || actionPending === `reject:${a.id}`} style={{ opacity: (actionPending === `approve:${a.id}` || actionPending === `reject:${a.id}`) ? 0.5 : 1 }}
                    onClick={async () => { setActionPending(`reject:${a.id}`); try { await api.put(`/auth/reject/${a.id}`); const res = await api.get('/auth/pending-approvals'); setPendingApprovals(res.data || []); } catch (err) { alert((he ? 'נכשל: ' : 'Failed: ') + (err.response?.data?.error || err.message)); } finally { setActionPending(null); } }}>
                    ✕ Reject
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>{t('auth.username')}</th><th>{t('settings.fullName')}</th><th>{t('settings.role')}</th><th className="text-center">{t('settings.active')}</th><th>{t('settings.created')}</th><th></th></tr>
              </thead>
              <tbody>
                {users.map(u => {
                  const isEd = editingUser === u.id;
                  return (
                    <tr key={u.id}>
                      <td className="text-gold">{u.username}</td>
                      <td>
                        {isEd ? (
                          <input className="inline-edit" defaultValue={u.full_name}
                            onBlur={e => updateUser(u.id, { fullName: e.target.value })} />
                        ) : u.full_name || '--'}
                      </td>
                      <td>
                        {isEd ? (
                          <select className="inline-edit" defaultValue={u.role}
                            onChange={e => updateUser(u.id, { role: e.target.value })}>
                            {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                          </select>
                        ) : <span className="badge" style={{ background: 'var(--navy-light)', color: 'var(--gold)' }}>{u.role}</span>}
                      </td>
                      <td className="text-center">
                        <input type="checkbox" checked={u.is_active !== false}
                          onChange={e => updateUser(u.id, { isActive: e.target.checked })} />
                      </td>
                      <td className="text-muted">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '--'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn btn-outline btn-sm" onClick={() => setEditingUser(isEd ? null : u.id)}>
                            {isEd ? t('settings.done') : t('common.edit')}
                          </button>
                          <button className="btn btn-outline btn-sm" onClick={() => resetPassword(u.id)}>{t('settings.resetPw')}</button>
                          {u.role !== 'admin' && (
                            <button className="btn btn-sm btn-danger" onClick={async () => {
                              if (!window.confirm(`Delete user "${u.username}"? This cannot be undone.`)) return;
                              try { await api.delete(`/auth/users/${u.id}`); fetchUsers(); } catch (err) { alert('Failed: ' + (err.response?.data?.error || err.message)); }
                            }}>Delete</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══ Stock Tab — S&T + Shager sub-tabs ═══ */}
      {tab === 'stock' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className={`btn btn-sm ${stockTab === 'combined' ? 'btn-gold' : 'btn-outline'}`} onClick={() => setStockTab('combined')}>{he ? 'טבלה מאוחדת' : 'Combined'} ({new Set([...stockData.map(s=>s.sku), ...shagerStockData.map(s=>s.sku)]).size})</button>
            <button className={`btn btn-sm ${stockTab === 'st' ? 'btn-gold' : 'btn-outline'}`} onClick={() => setStockTab('st')}>S&T Stock ({stockData.length})</button>
            <button className={`btn btn-sm ${stockTab === 'shager' ? 'btn-gold' : 'btn-outline'}`} onClick={() => setStockTab('shager')}>Shager Stock ({shagerStockData.length})</button>
            <div style={{ flex: 1 }} />
            <input ref={(el) => (window._totalStockFile = el)} type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                if (!window.confirm(he
                  ? `העלאת ספירת מלאי כוללת — תחליף לחלוטין את S&T + Shager לכל ברקוד שמופיע בקובץ. SKUs שלא בקובץ — לא ייגעו.\n\nלהמשיך עם "${file.name}"?`
                  : `Total Stock Upload — REPLACES S&T + Shager for every barcode in the file. SKUs not in file are untouched.\n\nProceed with "${file.name}"?`)) {
                  e.target.value = ''; return;
                }
                setStockUploading(true);
                try {
                  const fd = new FormData(); fd.append('file', file);
                  const r = await api.post('/warehouse/stock/upload-total', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                  const d = r.data;
                  let msg = he
                    ? `✓ עודכנו: ${d.matched}\n✗ לא נמצאו: ${d.notFound}\nMANUAL order: ${d.manualOrderNo}`
                    : `✓ Updated: ${d.matched}\n✗ Not found: ${d.notFound}\nMANUAL order: ${d.manualOrderNo}`;
                  if (d.notFound > 0 && d.details?.notFound) {
                    msg += '\n\n' + (he ? 'ברקודים שלא נמצאו:' : 'Barcodes not found:') + '\n';
                    msg += d.details.notFound.map(n => `  ${n.barcode} — ${n.name || ''}`).join('\n');
                  }
                  alert(msg);
                  fetchStock();
                } catch (err) {
                  alert((he ? 'שגיאה: ' : 'Error: ') + (err.response?.data?.error || err.message));
                } finally {
                  setStockUploading(false);
                }
                e.target.value = '';
              }} />
            <button className="btn btn-sm" disabled={stockUploading} style={{ background: '#1d4ed8', color: '#fff', borderColor: '#1d4ed8', opacity: stockUploading ? 0.6 : 1 }}
              onClick={() => window._totalStockFile?.click()}>
              {stockUploading ? '⏳ ' : '📥 '}{stockUploading ? (he ? 'מעלה…' : 'Uploading…') : (he ? 'העלה ספירת מלאי כוללת' : 'Upload Total Stock')}
            </button>
          </div>

          {/* Combined view: S&T + Shager + Total per SKU, inline-editable */}
          {stockTab === 'combined' && (() => {
            const map = new Map();
            for (const s of stockData) {
              map.set(s.sku, { sku: s.sku, description: s.description, barcode: s.barcode, display_sku: s.display_sku, st: s, shager: null });
            }
            for (const s of shagerStockData) {
              if (map.has(s.sku)) map.get(s.sku).shager = s;
              else map.set(s.sku, { sku: s.sku, description: s.description, barcode: s.barcode, display_sku: s.display_sku, st: null, shager: s });
            }
            // External-warehouse (pallet) stock from stock-summary — read-only here, edited in the External Warehouse tab.
            // Index by sku + alias skus so external lines up with the right row; surface SKUs that live ONLY in external.
            const extMap = new Map();
            for (const s of stockSummary) {
              const ext = (parseFloat(s.externalSt) || 0) + (parseFloat(s.externalShager) || 0);
              extMap.set(s.sku, ext);
              for (const a of (s.aliasSkus || [])) if (!extMap.has(a)) extMap.set(a, ext);
              if (ext > 0 && !map.has(s.sku) && !(s.aliasSkus || []).some(a => map.has(a))) {
                map.set(s.sku, { sku: s.sku, description: s.description, barcode: s.barcode, display_sku: s.display_sku || s.sku, st: null, shager: null });
              }
            }
            const extOf = (r) => extMap.get(r.sku) || 0;
            // Pallets per SKU (for editing external stock). effective units = override ?? total_units.
            const palletsBySku = new Map();
            for (const p of extPallets) {
              const eff = parseFloat(p.effective_total_units != null ? p.effective_total_units : (p.total_units_override != null ? p.total_units_override : p.total_units)) || 0;
              if (!palletsBySku.has(p.sku)) palletsBySku.set(p.sku, []);
              palletsBySku.get(p.sku).push({ ...p, eff });
            }
            const ssBySku = new Map();
            for (const s of stockSummary) ssBySku.set(s.sku, s);
            const palletsFor = (r) => {
              const ss = ssBySku.get(r.sku);
              const skus = new Set([r.sku, ...((ss && ss.aliasSkus) || [])]);
              let list = [];
              for (const sk of skus) list = list.concat(palletsBySku.get(sk) || []);
              return list;
            };
            const savePallet = async (p, val) => {
              const v = parseInt(val);
              if (isNaN(v) || v < 0 || v === p.eff) return;
              try { await api.put(`/external-warehouse/pallets/${p.id}`, { total_units_override: v }); fetchStock(); }
              catch (err) { alert((he ? 'נכשל: ' : 'Failed: ') + (err.response?.data?.error || err.message)); }
            };
            const allRows = Array.from(map.values());
            const dir = combinedSort.dir === 'asc' ? 1 : -1;
            const cmp = (a, b) => {
              const f = combinedSort.field;
              if (f === 'sku') return String(a.sku).localeCompare(String(b.sku)) * dir;
              if (f === 'description') return String(a.description || '').localeCompare(String(b.description || ''), undefined, { sensitivity: 'base' }) * dir;
              const stOf = (r) => r.st ? parseFloat(r.st.quantity) || 0 : 0;
              const shOf = (r) => r.shager ? parseFloat(r.shager.quantity) || 0 : 0;
              const valOf = (r, kind) => {
                if (kind === 'st') return stOf(r);
                if (kind === 'shager') return shOf(r);
                if (kind === 'external') return extOf(r);
                return stOf(r) + shOf(r) + extOf(r);
              };
              return (valOf(a, f) - valOf(b, f)) * dir;
            };
            const rows = allRows.sort(cmp);
            const totalST = rows.reduce((n, r) => n + (r.st ? parseFloat(r.st.quantity) || 0 : 0), 0);
            const totalShager = rows.reduce((n, r) => n + (r.shager ? parseFloat(r.shager.quantity) || 0 : 0), 0);
            const totalExt = rows.reduce((n, r) => n + extOf(r), 0);
            const totalAll = totalST + totalShager + totalExt;
            const arrow = (f) => combinedSort.field === f ? (combinedSort.dir === 'asc' ? ' ▲' : ' ▼') : '';
            const onSort = (f) => setCombinedSort(p => ({ field: f, dir: p.field === f && p.dir === 'asc' ? 'desc' : 'asc' }));
            return (
              <div>
                <div className="flex-between mb-16">
                  <h2 style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, letterSpacing: 1 }}>{he ? 'מלאי משולב — S&T + Shager (מחסן)' : 'COMBINED STOCK — S&T + Shager (warehouse)'}</h2>
                  <div className="text-muted" style={{ fontSize: 12 }}>{rows.length} SKUs</div>
                </div>
                <div className="text-muted" style={{ fontSize: 11, marginBottom: 8 }}>
                  {he ? 'ערוך ידנית כדי לתקן מלאי. S&T = מלאי מקומי (ניתן לעריכה). Shager = מלאי המחסן החיצוני (משטחים) — ערוך ישירות, או לחץ על מספר ה-📦 כדי לערוך כל משטח בנפרד.' : 'Edit manually to correct stock. S&T = local stock (editable). Shager = the external warehouse (pallet) stock — edit inline, or click the 📦 number to edit each pallet.'}
                </div>
                {rows.length === 0 ? <div className="empty-state">{t('common.noData')}</div> : (
                  <div className="table-wrap"><table><thead><tr>
                    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => onSort('sku')}>{t('common.sku')}{arrow('sku')}</th>
                    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => onSort('description')}>{t('common.product')}{arrow('description')}</th>
                    <th className="text-end" style={{ color: '#1565c0', cursor: 'pointer', userSelect: 'none' }} onClick={() => onSort('st')}>S&T{arrow('st')}</th>
                    <th className="text-end" style={{ color: '#4caf50', cursor: 'pointer', userSelect: 'none' }} onClick={() => onSort('external')}>Shager{arrow('external')}</th>
                    <th className="text-end" style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => onSort('total')}>{he ? 'סה"כ' : 'Total'}{arrow('total')}</th>
                  </tr></thead><tbody>
                    {rows.map(r => {
                      const stQty = r.st ? parseFloat(r.st.quantity) || 0 : 0;
                      const shQty = r.shager ? parseFloat(r.shager.quantity) || 0 : 0;
                      const extQty = extOf(r);
                      const pals = palletsFor(r);
                      return (
                        <Fragment key={r.sku}>
                        <tr>
                          <td>
                            <div>{r.display_sku || r.sku}</div>
                            {r.barcode && <div className="text-muted" style={{ fontSize: 11 }}>{r.barcode}</div>}
                          </td>
                          <td>{r.description}</td>
                          <td className="text-end fw-bold">
                            <input className="inline-edit" type="number" min="0" style={{ width: 70, textAlign: 'right', color: '#1565c0' }}
                              key={`cst-${r.st?.id || 'new'}-${stQty}`} defaultValue={stQty}
                              onBlur={async e => {
                                const v = parseFloat(e.target.value);
                                if (isNaN(v) || v < 0 || v === stQty) return;
                                try {
                                  if (r.st) {
                                    await api.put(`/warehouse/existing-stock/${r.st.id}`, { quantity: v });
                                  } else {
                                    await api.post('/warehouse/existing-stock', { sku: r.sku, description: r.description, quantity: v, origin: 'EU' });
                                  }
                                  fetchStock();
                                } catch (err) { alert((he ? 'נכשל: ' : 'Failed: ') + (err.response?.data?.error || err.message)); }
                              }} />
                          </td>
                          <td className="text-end fw-bold" style={{ color: '#4caf50' }}>
                            {pals.length === 1 ? (
                              <input className="inline-edit" type="number" min="0" style={{ width: 70, textAlign: 'right', color: '#4caf50' }}
                                key={`ext-${pals[0].id}-${pals[0].eff}`} defaultValue={pals[0].eff}
                                onBlur={e => savePallet(pals[0], e.target.value)} />
                            ) : pals.length > 1 ? (
                              <button type="button" onClick={() => setExpandedExtSku(expandedExtSku === r.sku ? null : r.sku)}
                                style={{ background: 'none', border: 'none', color: '#4caf50', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit' }}>
                                {extQty} <span style={{ fontSize: 10 }}>({pals.length}📦 {expandedExtSku === r.sku ? '▲' : '▼'})</span>
                              </button>
                            ) : (extQty || '')}
                          </td>
                          <td className="text-end fw-bold">{stQty + shQty + extQty}</td>
                        </tr>
                        {expandedExtSku === r.sku && pals.length > 1 && (
                          <tr style={{ background: '#faf5ff' }}>
                            <td colSpan={5} style={{ padding: '8px 16px' }}>
                              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: '#7e22ce' }}>{he ? 'משטחים חיצוניים — ערוך כל משטח בנפרד:' : 'External pallets — edit each one:'}</div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                                {pals.map(p => (
                                  <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 6, border: '1px solid #e9d5ff', borderRadius: 6, padding: '4px 8px' }}>
                                    <span style={{ fontSize: 11, color: '#64748b' }}>#{p.id} · {p.owner || '—'}{p.matched_si_order_no ? ' · ' + p.matched_si_order_no : ''}</span>
                                    <input className="inline-edit" type="number" min="0" style={{ width: 70, textAlign: 'right', color: '#4caf50' }}
                                      key={`extp-${p.id}-${p.eff}`} defaultValue={p.eff}
                                      onBlur={e => savePallet(p, e.target.value)} />
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                        </Fragment>
                      );
                    })}
                    <tr style={{ background: 'rgba(0,0,0,0.04)', fontWeight: 700 }}>
                      <td colSpan={2} style={{ textAlign: 'end' }}>{he ? 'סה"כ' : 'TOTAL'}</td>
                      <td className="text-end" style={{ color: '#1565c0' }}>{totalST}</td>
                      <td className="text-end" style={{ color: '#4caf50' }}>{totalExt}</td>
                      <td className="text-end">{totalAll}</td>
                    </tr>
                  </tbody></table></div>
                )}
              </div>
            );
          })()}

          {/* S&T Stock */}
          {stockTab === 'st' && (
            <div>
              <div className="flex-between mb-16">
                <h2 style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, letterSpacing: 1 }}>S&T STOCK</h2>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input ref={stockFileRef} type="file" accept=".xlsx,.xls" onChange={uploadStockExcel} style={{ display: 'none' }} />
                  <button className="btn btn-outline" onClick={() => stockFileRef.current?.click()}>Upload Excel</button>
                </div>
              </div>
              <div className="card" style={{ marginBottom: 16, padding: 16 }}>
                <form onSubmit={addExistingStock} style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'end' }}>
                  <div><label style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 4, display: 'block' }}>{t('common.sku')}</label><input className="input" value={stockForm.sku} onChange={e => setStockForm(p => ({...p, sku: e.target.value}))} required style={{ width: 100 }} /></div>
                  <div><label style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 4, display: 'block' }}>{t('common.product')}</label><input className="input" value={stockForm.description} onChange={e => setStockForm(p => ({...p, description: e.target.value}))} style={{ width: 200 }} /></div>
                  <div><label style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 4, display: 'block' }}>{t('warehouse.qty')}</label><input className="input" type="number" value={stockForm.quantity} onChange={e => setStockForm(p => ({...p, quantity: e.target.value}))} required style={{ width: 80 }} /></div>
                  <div><label style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 4, display: 'block' }}>Unit Cost</label><input className="input" type="number" step="0.01" value={stockForm.unitCost} onChange={e => setStockForm(p => ({...p, unitCost: e.target.value}))} style={{ width: 80 }} /></div>
                  <div><label style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 4, display: 'block' }}>{t('logistics.origin')}</label><select className="input" value={stockForm.origin} onChange={e => setStockForm(p => ({...p, origin: e.target.value}))} style={{ width: 70 }}><option value="EU">EU</option><option value="CN">CN</option></select></div>
                  <button type="submit" className="btn btn-gold">Add</button>
                </form>
              </div>
              {stockData.length === 0 ? <div className="empty-state">{t('common.noData')}</div> : (
                <div className="table-wrap"><table><thead><tr>
                  <th>{t('common.sku')}</th><th>{t('common.product')}</th><th className="text-end">{t('warehouse.qty')}</th>
                  <th className="text-end">Unit Cost</th><th>{t('logistics.origin')}</th><th>{t('warehouse.notes')}</th><th></th>
                </tr></thead><tbody>
                  {stockData.map(item => (
                    <tr key={item.id}>
                      <td>{item.display_sku || item.sku}</td><td>{item.description}</td>
                      <td className="text-end fw-bold"><input className="inline-edit" type="number" style={{width:70, textAlign:'right'}} key={`qty-${item.id}-${item.quantity}`} defaultValue={parseFloat(item.quantity)} onBlur={e => { const v = parseFloat(e.target.value); if (v !== parseFloat(item.quantity)) api.put(`/warehouse/existing-stock/${item.id}`, { quantity: v }).then(() => fetchStock()); }} /></td>
                      <td className="text-end"><input className="inline-edit" type="number" step="0.01" style={{width:80, textAlign:'right'}} key={`cost-${item.id}-${item.unit_cost}`} defaultValue={item.unit_cost ? parseFloat(item.unit_cost) : ''} placeholder="—" onBlur={e => { const v = parseFloat(e.target.value); if (!isNaN(v) && v !== parseFloat(item.unit_cost || 0)) api.put(`/warehouse/existing-stock/${item.id}`, { unitCost: v }).then(() => fetchStock()); }} /></td>
                      <td><span className={`badge badge-${(item.origin || '').toLowerCase()}`}>{item.origin}</span></td>
                      <td className="text-muted"><input className="inline-edit" style={{width:100}} key={`notes-${item.id}-${item.notes}`} defaultValue={item.notes || ''} placeholder="Notes..." onBlur={e => { if (e.target.value !== (item.notes || '')) api.put(`/warehouse/existing-stock/${item.id}`, { notes: e.target.value }).then(() => fetchStock()); }} /></td>
                      <td><button className="btn btn-sm btn-danger" onClick={() => deleteStock(item.id)}>{t('common.delete')}</button></td>
                    </tr>
                  ))}
                </tbody></table></div>
              )}
            </div>
          )}

          {/* Shager Stock */}
          {stockTab === 'shager' && (
            <div>
              <div className="flex-between mb-16">
                <h2 style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, letterSpacing: 1 }}>SHAGER STOCK</h2>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input ref={shagerFileRef} type="file" accept=".xlsx,.xls" onChange={uploadShagerExcel} style={{ display: 'none' }} />
                  <button className="btn btn-outline" onClick={() => shagerFileRef.current?.click()}>Upload Excel</button>
                </div>
              </div>
              <p className="text-muted" style={{ fontSize: 13, marginBottom: 16 }}>Shager stock from received orders. Upload Excel with SKU + Quantity to update levels.</p>
              {shagerStockData.length === 0 ? <div className="empty-state">{t('common.noData')}</div> : (
                <div className="table-wrap"><table><thead><tr>
                  <th>{t('portal.order')}</th><th>{t('common.sku')}</th><th>{t('common.product')}</th>
                  <th className="text-end">{t('warehouse.qty')}</th><th className="text-end">Unit Cost</th><th>{t('logistics.origin')}</th>
                </tr></thead><tbody>
                  {shagerStockData.map(item => (
                    <tr key={item.id}>
                      <td className="text-muted" style={{ fontSize: 12 }}>{item.order_no}</td>
                      <td>{item.display_sku || item.sku}</td><td>{item.description}</td>
                      <td className="text-end fw-bold"><input className="inline-edit" type="number" style={{width:70, textAlign:'right'}} key={`sq-${item.id}-${item.quantity}`} defaultValue={parseFloat(item.quantity)} onBlur={e => { const v = parseFloat(e.target.value); if (v !== parseFloat(item.quantity)) api.put(`/warehouse/received-stock/${item.id}`, { quantity: v }).then(() => fetchStock()); }} /></td>
                      <td className="text-end"><input className="inline-edit" type="number" step="0.01" style={{width:80, textAlign:'right'}} key={`sc-${item.id}-${item.unit_cost}`} defaultValue={item.unit_cost ? parseFloat(item.unit_cost) : ''} placeholder="—" onBlur={e => { const v = parseFloat(e.target.value); if (!isNaN(v) && v !== parseFloat(item.unit_cost || 0)) api.put(`/warehouse/received-stock/${item.id}`, { unitCost: v }).then(() => fetchStock()); }} /></td>
                      <td><span className={`badge badge-${(item.origin || '').toLowerCase()}`}>{item.origin}</span></td>
                    </tr>
                  ))}
                </tbody></table></div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ═══ Product Dimensions Tab ═══ */}
      {tab === 'dimensions' && (
        <div>
          <div className="flex-between mb-16">
            <h2 style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, letterSpacing: 1 }}>{he ? 'מידות מוצרים' : 'PRODUCT DIMENSIONS'}</h2>
            <div style={{ display: 'flex', gap: 8 }}>
              <input ref={dimensionsFileRef} type="file" accept=".xlsx,.xls" onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                try {
                  const XLSX = (await import('xlsx'));
                  const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
                  const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);
                  const items = [];
                  for (const row of rows) {
                    const barcode = String(row['__EMPTY_8'] || row['Barcode'] || row['barcode'] || row['EAN'] || '').trim();
                    const grossWeight = parseFloat(row['__EMPTY_7'] || row['Weight'] || row['weight_kg'] || 0);
                    const cbmPerBox = parseFloat(row['__EMPTY_26'] || row['CBM'] || 0);
                    const unitsPerBox = parseFloat(row['__EMPTY_10'] || row['Units'] || 1);
                    const cbmPerUnit = cbmPerBox / unitsPerBox;
                    const weightPerUnit = grossWeight / unitsPerBox;
                    if (barcode && grossWeight > 0) items.push({ barcode, cbmPerUnit, weightKg: weightPerUnit });
                  }
                  if (!items.length) { alert('No valid items found'); return; }
                  const res = await api.post('/settings/dimensions/bulk', { items });
                  alert(`Updated ${res.data?.updated || 0} products`);
                  fetchDimensions();
                } catch (err) { alert('Failed: ' + (err.response?.data?.error || err.message)); }
                e.target.value = '';
              }} style={{ display: 'none' }} />
              <button className="btn btn-outline" onClick={() => dimensionsFileRef.current?.click()}>{he ? 'העלאת אקסל' : 'Upload Marshall Excel'}</button>
            </div>
          </div>
          <p className="text-muted" style={{ fontSize: 13, marginBottom: 16 }}>
            CBM and Weight <strong>per unit</strong>. Used for pallet and container calculations.
          </p>
          {dimensions.length === 0 ? <div className="empty-state">{t('common.noData')}</div> : (
            <div className="table-wrap"><table><thead><tr>
              <th>SKU</th><th>Product</th><th>Barcode</th>
              <th className="text-end">CBM/unit</th><th className="text-end">Weight (kg)</th>
            </tr></thead><tbody>
              {dimensions.map(d => (
                <tr key={d.sku}>
                  <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 13 }}>{d.sku}</td>
                  <td>{d.description}</td>
                  <td className="text-muted" style={{ fontSize: 12 }}>{d.barcode || '—'}</td>
                  <td className="text-end">
                    <input className="inline-edit" type="number" step="0.000001" style={{ width: 100, textAlign: 'right' }}
                      key={`cbm-${d.sku}-${d.cbm_per_unit}`}
                      defaultValue={parseFloat(d.cbm_per_unit) || ''}
                      placeholder="0"
                      onBlur={e => {
                        const v = parseFloat(e.target.value) || 0;
                        if (v !== parseFloat(d.cbm_per_unit || 0))
                          api.put('/settings/dimensions/' + d.sku, { cbmPerUnit: v }).then(() => fetchDimensions());
                      }} />
                  </td>
                  <td className="text-end">
                    <input className="inline-edit" type="number" step="0.001" style={{ width: 80, textAlign: 'right' }}
                      key={`wkg-${d.sku}-${d.weight_kg}`}
                      defaultValue={parseFloat(d.weight_kg) || ''}
                      placeholder="0"
                      onBlur={e => {
                        const v = parseFloat(e.target.value) || 0;
                        if (v !== parseFloat(d.weight_kg || 0))
                          api.put('/settings/dimensions/' + d.sku, { weightKg: v }).then(() => fetchDimensions());
                      }} />
                  </td>
                </tr>
              ))}
            </tbody></table></div>
          )}
        </div>
      )}

      {/* ═══ Pricing Tab ═══ */}
      {tab === 'pricing' && <PricingPage />}

      {tab === 'shipping' && <ShippingCosts />}
      {tab === 'shipcalc' && <ShippingCalculator he={he} />}
      {tab === 'landed' && <LandedCosts />}
      {tab === 'expenses' && <ExpensesTab he={he} />}
      {tab === 'upload' && <UploadCenter />}
      {tab === 'email' && <EmailRequests />}
      {tab === 'apikeys' && <ApiKeysTab />}
      {tab === 'csim' && <ContainerSimulator />}
    </div>
  );
}

function ShippingCosts() {
  const { i18n } = useTranslation();
  const he = i18n.language === 'he';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('total');
  const [sortDir, setSortDir] = useState('desc');
  const [importMode, setImportMode] = useState('sea'); // 'air' | 'sea'
  const [exportMode, setExportMode] = useState('sea'); // 'air' | 'sea'
  const [rates, setRates] = useState(null);
  const [rateEdit, setRateEdit] = useState({ importSeaPer40hc: '', exportSeaPer40hc: '', importAirPerKg: '', exportAirPerKg: '' });
  const [savingRates, setSavingRates] = useState(false);

  const fetchAll = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get('/settings/shipping-analysis'),
      api.get('/settings/shipping-rates'),
    ]).then(([a, r]) => {
      setData(a.data);
      setRates(r.data);
      setRateEdit({
        importSeaPer40hc: r.data.importSeaPer40hc,
        exportSeaPer40hc: r.data.exportSeaPer40hc,
        importAirPerKg: r.data.importAirPerKg,
        exportAirPerKg: r.data.exportAirPerKg,
      });
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveRates = async () => {
    setSavingRates(true);
    try {
      await api.put('/settings/shipping-rates', {
        importSeaPer40hc: parseFloat(rateEdit.importSeaPer40hc),
        exportSeaPer40hc: parseFloat(rateEdit.exportSeaPer40hc),
        importAirPerKg: parseFloat(rateEdit.importAirPerKg),
        exportAirPerKg: parseFloat(rateEdit.exportAirPerKg),
      });
      fetchAll();
    } catch (e) { alert((he ? 'נכשל: ' : 'Failed: ') + (e.response?.data?.error || e.message)); }
    setSavingRates(false);
  };

  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (!data || !data.length) return <div className="empty-state">No shipping data yet</div>;

  const fmt2 = (n) => n > 0 ? `$${n.toFixed(2)}` : '—';
  const searchLower = search.toLowerCase();
  const filtered = data.filter(p => !searchLower ||
    (p.description || '').toLowerCase().includes(searchLower) ||
    (p.sku || '').toLowerCase().includes(searchLower) ||
    (p.barcode || '').toLowerCase().includes(searchLower)
  );

  // Calculate combined total based on selected route
  const withTotal = filtered.map(p => {
    const imp = importMode === 'air' ? (p.importAir || 0) : (p.importSea || 0);
    const exp = exportMode === 'air' ? (p.exportAir || 0) : (p.exportSea || 0);
    const purchase = p.purchasePrice || 0;
    return { ...p, routeImport: imp, routeExport: exp, routeTotal: Math.round((purchase + imp + exp) * 100) / 100 };
  });

  const toggleSort = (field) => {
    if (sortBy === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortDir('desc'); }
  };
  const sorted = [...withTotal].sort((a, b) => {
    let va = a[sortBy], vb = b[sortBy];
    if (typeof va === 'string') { va = (va || '').toLowerCase(); vb = (vb || '').toLowerCase(); }
    else { va = va || 0; vb = vb || 0; }
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });
  const si = (f) => sortBy === f ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
  const mono = { fontFamily: "'DM Mono', monospace", fontSize: 12 };

  const routeBtn = (label, icon, mode, current, setter) => (
    <button onClick={() => setter(mode)} style={{
      padding: '6px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
      border: current === mode ? 'none' : '1px solid #e2e8f0',
      background: current === mode ? (mode === 'air' ? '#3b82f6' : '#22c55e') : '#fff',
      color: current === mode ? '#fff' : '#64748b',
    }}>{icon} {label}</button>
  );

  const routeLabel = `${importMode === 'air' ? '✈' : '🚢'} Import → ${exportMode === 'air' ? '✈' : '🚢'} Export`;

  return (
    <div>
      <h2 style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, letterSpacing: 1, marginBottom: 16 }}>{he ? 'מחשבון עלויות שילוח' : 'SHIPPING COST CALCULATOR'}</h2>

      {/* Route selector */}
      <div className="card" style={{ padding: 20, marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 40, alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 11, textTransform: 'uppercase', fontWeight: 700, color: '#6b7280', marginBottom: 8 }}>{he ? 'ייבוא לישראל' : 'Import to Israel'}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {routeBtn(he ? 'אוויר' : 'Air', '✈', 'air', importMode, setImportMode)}
              {routeBtn(he ? 'ים' : 'Ocean', '🚢', 'sea', importMode, setImportMode)}
            </div>
          </div>
          <div style={{ fontSize: 28, color: '#d1d5db' }}>→</div>
          <div>
            <div style={{ fontSize: 11, textTransform: 'uppercase', fontWeight: 700, color: '#6b7280', marginBottom: 8 }}>{he ? 'ייצוא ללקוח' : 'Export to Customer'}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {routeBtn(he ? 'אוויר' : 'Air', '✈', 'air', exportMode, setExportMode)}
              {routeBtn(he ? 'ים' : 'Ocean', '🚢', 'sea', exportMode, setExportMode)}
            </div>
          </div>
          <div style={{ flex: 1 }} />
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 20, fontWeight: 800 }}>{routeLabel}</div>
            <div className="text-muted" style={{ fontSize: 12 }}>
              Import: {importMode === 'air' ? `$${rates?.importAirPerKg}/kg` : `$${(rates?.importSeaPer40hc || 0).toLocaleString()}/${rates?.usableCbm} CBM`} · Export: {exportMode === 'air' ? `$${rates?.exportAirPerKg}/kg` : `$${(rates?.exportSeaPer40hc || 0).toLocaleString()}/${rates?.usableCbm} CBM`}
            </div>
          </div>
        </div>
      </div>

      {/* Editable rates — feed the per-product Import/Export costs below */}
      <div className="card" style={{ padding: 16, marginBottom: 20 }}>
        <h3 style={{ marginTop: 0 }}>{he ? 'תעריפי שילוח (ניתן לעריכה)' : 'Shipping Rates (editable)'}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, alignItems: 'end' }}>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-dim)', display: 'block', marginBottom: 4 }}>🚢 {he ? 'ים — יבוא ($/קונטיינר)' : 'Sea import ($/container)'}</label>
            <input className="input" type="number" step="0.01" min="0" value={rateEdit.importSeaPer40hc}
              onChange={e => setRateEdit(p => ({ ...p, importSeaPer40hc: e.target.value }))} />
            <div className="text-muted" style={{ fontSize: 10, marginTop: 4 }}>= ${(parseFloat(rateEdit.importSeaPer40hc) / (rates?.usableCbm || 67.25) || 0).toFixed(2)}/CBM</div>
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-dim)', display: 'block', marginBottom: 4 }}>🚢 {he ? 'ים — יצוא ($/קונטיינר)' : 'Sea export ($/container)'}</label>
            <input className="input" type="number" step="0.01" min="0" value={rateEdit.exportSeaPer40hc}
              onChange={e => setRateEdit(p => ({ ...p, exportSeaPer40hc: e.target.value }))} />
            <div className="text-muted" style={{ fontSize: 10, marginTop: 4 }}>= ${(parseFloat(rateEdit.exportSeaPer40hc) / (rates?.usableCbm || 67.25) || 0).toFixed(2)}/CBM</div>
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-dim)', display: 'block', marginBottom: 4 }}>✈ {he ? 'אוויר — יבוא ($/ק"ג)' : 'Air import ($/kg)'}</label>
            <input className="input" type="number" step="0.01" min="0" value={rateEdit.importAirPerKg}
              onChange={e => setRateEdit(p => ({ ...p, importAirPerKg: e.target.value }))} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-dim)', display: 'block', marginBottom: 4 }}>✈ {he ? 'אוויר — יצוא ($/ק"ג)' : 'Air export ($/kg)'}</label>
            <input className="input" type="number" step="0.01" min="0" value={rateEdit.exportAirPerKg}
              onChange={e => setRateEdit(p => ({ ...p, exportAirPerKg: e.target.value }))} />
          </div>
          <div>
            <button className="btn btn-gold" disabled={savingRates} onClick={saveRates}>{savingRates ? '⏳' : '💾'} {he ? 'שמור' : 'Save'}</button>
          </div>
        </div>
        <div className="text-muted" style={{ fontSize: 11, marginTop: 8 }}>
          {he ? `קיבולת קונטיינר ${rates?.usableCbm} CBM (40HC). התעריפים מזינים את טבלת עלויות היבוא/יצוא למטה.` : `Container capacity ${rates?.usableCbm} CBM (40HC). These rates drive the Import/Export cost table below.`}
        </div>
      </div>

      <div className="flex-between" style={{ marginBottom: 12 }}>
        <span className="text-muted" style={{ fontSize: 12 }}>{sorted.length} products</span>
        <input className="input" placeholder={he ? 'חפש מוצר, מק"ט...' : 'Search product, SKU...'} value={search} onChange={e => setSearch(e.target.value)} style={{ maxWidth: 250 }} />
      </div>

      <div className="table-wrap">
        <table>
          <thead><tr>
            <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('sku')}>SKU{si('sku')}</th>
            <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('description')}>Product{si('description')}</th>
            <th className="text-end" style={{ cursor: 'pointer' }} onClick={() => toggleSort('purchasePrice')}>{he ? 'רכישה' : 'Purchase'}{si('purchasePrice')}</th>
            <th className="text-end" style={{ cursor: 'pointer' }} onClick={() => toggleSort('routeImport')}>{he ? 'ייבוא' : 'Import'}{si('routeImport')}</th>
            <th className="text-end" style={{ cursor: 'pointer' }} onClick={() => toggleSort('routeExport')}>{he ? 'ייצוא' : 'Export'}{si('routeExport')}</th>
            <th className="text-end fw-bold" style={{ cursor: 'pointer', borderBottom: '2px solid #333' }} onClick={() => toggleSort('routeTotal')}>{he ? 'עלות כוללת' : 'Total Cost'}{si('routeTotal')}</th>
          </tr></thead>
          <tbody>
            {sorted.map(p => (
              <tr key={p.sku}>
                <td>
                  <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 13 }}>{p.sku}</div>
                  {p.barcode && p.barcode !== p.sku && <div className="text-muted" style={{ fontSize: 10 }}>{p.barcode}</div>}
                </td>
                <td>{p.description}</td>
                <td className="text-end" style={mono}>{fmt2(p.purchasePrice)}</td>
                <td className="text-end" style={mono}>{fmt2(p.routeImport)}</td>
                <td className="text-end" style={mono}>{fmt2(p.routeExport)}</td>
                <td className="text-end fw-bold" style={{ ...mono, fontSize: 14 }}>{fmt2(p.routeTotal)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ════════════════ Shipping Calculator (container-based per-CBM) ════════════════
function ShippingCalculator({ he }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [edit, setEdit] = useState({ containerCbmCapacity: '', euContainerCost: '', cnContainerCost: '' });
  const [search, setSearch] = useState('');
  const [originFilter, setOriginFilter] = useState('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/settings/shipping-calculator');
      setData(r.data);
      setEdit({
        containerCbmCapacity: r.data.settings.containerCbmCapacity,
        euContainerCost: r.data.settings.euContainerCost,
        cnContainerCost: r.data.settings.cnContainerCost,
      });
    } catch {}
    setLoading(false);
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/settings/shipping-calculator', {
        containerCbmCapacity: parseFloat(edit.containerCbmCapacity),
        euContainerCost: parseFloat(edit.euContainerCost),
        cnContainerCost: parseFloat(edit.cnContainerCost),
      });
      await fetchData();
    } catch (e) { alert((he ? 'נכשל: ' : 'Failed: ') + (e.response?.data?.error || e.message)); }
    setSaving(false);
  };

  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (!data) return <div className="empty-state">No data</div>;

  const fmtUsd = (v) => v == null || isNaN(v) ? '—' : '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const filtered = (data.products || []).filter(p => {
    if (originFilter !== 'all' && (p.origin || 'UNKNOWN') !== originFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return String(p.sku).toLowerCase().includes(q) || String(p.description || '').toLowerCase().includes(q);
  });

  return (
    <div>
      <h2 style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, letterSpacing: 1, marginBottom: 12 }}>
        🧮 {he ? 'מחשבון שילוח' : 'SHIPPING CALCULATOR'}
      </h2>
      <p className="text-muted" style={{ fontSize: 13, marginBottom: 16 }}>
        {he ? 'הוצאות יבוא לפי CBM × Container rate. ה-Settings האלה משמשים את הסימולטור.' : 'Import costs allocated by CBM × per-CBM rate. These settings drive the simulator landed-cost calculation.'}
      </p>

      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>{he ? 'הגדרות' : 'Container Settings'}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, alignItems: 'end' }}>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-dim)', display: 'block', marginBottom: 4 }}>{he ? 'קיבולת קונטיינר (CBM)' : 'Container Capacity (CBM)'}</label>
            <input className="input" type="number" step="0.01" min="0" value={edit.containerCbmCapacity}
              onChange={e => setEdit(p => ({ ...p, containerCbmCapacity: e.target.value }))} />
            <div className="text-muted" style={{ fontSize: 10, marginTop: 4 }}>40HC = 67 CBM</div>
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-dim)', display: 'block', marginBottom: 4 }}>{he ? 'EU Container ($)' : 'EU Container Cost ($)'}</label>
            <input className="input" type="number" step="0.01" min="0" value={edit.euContainerCost}
              onChange={e => setEdit(p => ({ ...p, euContainerCost: e.target.value }))} />
            <div className="text-muted" style={{ fontSize: 10, marginTop: 4 }}>= ${(parseFloat(edit.euContainerCost) / parseFloat(edit.containerCbmCapacity || 1)).toFixed(2)}/CBM</div>
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-dim)', display: 'block', marginBottom: 4 }}>{he ? 'CN Container ($)' : 'CN Container Cost ($)'}</label>
            <input className="input" type="number" step="0.01" min="0" value={edit.cnContainerCost}
              onChange={e => setEdit(p => ({ ...p, cnContainerCost: e.target.value }))} />
            <div className="text-muted" style={{ fontSize: 10, marginTop: 4 }}>= ${(parseFloat(edit.cnContainerCost) / parseFloat(edit.containerCbmCapacity || 1)).toFixed(2)}/CBM</div>
          </div>
          <div>
            <button className="btn btn-gold" disabled={saving} onClick={save}>{saving ? '⏳' : '💾'} {he ? 'שמור' : 'Save'}</button>
          </div>
        </div>
      </div>

      {/* Per-origin summary */}
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>{he ? 'סיכום לפי מקור (מלאי נוכחי)' : 'Per-Origin Summary (current stock)'}</h3>
        <div className="table-wrap"><table>
          <thead><tr><th>Origin</th><th className="text-end">Units</th><th className="text-end">Total CBM</th><th className="text-end">Container Cost</th><th className="text-end">$/CBM</th><th className="text-end">Total Shipping</th></tr></thead>
          <tbody>
            {['EU', 'CN'].map(orig => {
              const s = data.summary[orig] || { units: 0, cbm: 0, shipping: 0 };
              const cost = orig === 'EU' ? data.settings.euContainerCost : data.settings.cnContainerCost;
              const rate = orig === 'EU' ? data.settings.ratePerCbmEU : data.settings.ratePerCbmCN;
              return (
                <tr key={orig}>
                  <td style={{ fontWeight: 700 }}>{orig}</td>
                  <td className="text-end">{s.units}</td>
                  <td className="text-end">{s.cbm.toFixed(2)}</td>
                  <td className="text-end">{fmtUsd(cost)}</td>
                  <td className="text-end">{fmtUsd(rate)}</td>
                  <td className="text-end fw-bold">{fmtUsd(s.shipping)}</td>
                </tr>
              );
            })}
            {(data.summary.UNKNOWN?.units || 0) > 0 && (
              <tr style={{ background: '#fef2f2' }}>
                <td style={{ fontWeight: 700, color: '#991b1b' }}>UNKNOWN</td>
                <td className="text-end">{data.summary.UNKNOWN.units}</td>
                <td className="text-end">{data.summary.UNKNOWN.cbm.toFixed(2)}</td>
                <td colSpan={3} className="text-muted" style={{ fontSize: 11 }}>{he ? 'מקור חסר — הוסף ב-orders.origin של ה-SI' : 'Origin missing — fix orders.origin on the SI'}</td>
              </tr>
            )}
          </tbody>
        </table></div>
      </div>

      {/* Missing data warnings */}
      {(data.missing.cbm.length > 0 || data.missing.origin.length > 0) && (
        <div className="card" style={{ padding: 12, marginBottom: 16, background: '#fffbeb', border: '1px solid #fcd34d' }}>
          {data.missing.cbm.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <strong style={{ color: '#92400e' }}>⚠️ {data.missing.cbm.length} {he ? 'SKUs ללא CBM:' : 'SKUs missing CBM:'}</strong>{' '}
              {data.missing.cbm.map(p => p.sku).join(', ')}
            </div>
          )}
          {data.missing.origin.length > 0 && (
            <div>
              <strong style={{ color: '#92400e' }}>⚠️ {data.missing.origin.length} {he ? 'SKUs במלאי ללא מקור:' : 'SKUs in stock missing origin:'}</strong>{' '}
              {data.missing.origin.map(p => p.sku).join(', ')}
            </div>
          )}
        </div>
      )}

      {/* Per-SKU table */}
      <div className="card" style={{ padding: 16 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, marginInlineEnd: 'auto' }}>{he ? 'עלות שילוח פר SKU' : 'Per-SKU Shipping Cost'}</h3>
          <select className="input input-sm" value={originFilter} onChange={e => setOriginFilter(e.target.value)}>
            <option value="all">{he ? 'כל המקורות' : 'All Origins'}</option>
            <option value="EU">EU</option>
            <option value="CN">CN</option>
            <option value="UNKNOWN">{he ? 'ללא מקור' : 'Unknown'}</option>
          </select>
          <input className="input input-sm" placeholder={he ? '🔍 חיפוש' : '🔍 Search SKU / name'}
            value={search} onChange={e => setSearch(e.target.value)} style={{ width: 220 }} />
        </div>
        <div className="table-wrap"><table style={{ fontSize: 12 }}>
          <thead><tr>
            <th>SKU</th>
            <th>Product</th>
            <th>Origin</th>
            <th className="text-end">CBM/Unit</th>
            <th className="text-end">$/CBM</th>
            <th className="text-end">$/Unit Shipping</th>
            <th className="text-end">Stock</th>
            <th className="text-end">Total Shipping</th>
          </tr></thead>
          <tbody>
            {filtered.map(p => (
              <tr key={p.sku} style={{ background: !p.origin || !p.cbm ? '#fff7ed' : undefined }}>
                <td style={{ fontFamily: 'monospace' }}>{p.sku}</td>
                <td>{p.description}</td>
                <td><span className={`badge`} style={{ fontSize: 10, background: p.origin === 'EU' ? '#dbeafe' : p.origin === 'CN' ? '#fee2e2' : '#f3f4f6', color: p.origin === 'EU' ? '#1e3a8a' : p.origin === 'CN' ? '#991b1b' : '#6b7280', padding: '2px 6px', borderRadius: 4 }}>{p.origin || '—'}</span></td>
                <td className="text-end">{p.cbm ? p.cbm.toFixed(4) : '—'}</td>
                <td className="text-end">{p.origin ? fmtUsd(p.ratePerCbm) : '—'}</td>
                <td className="text-end fw-bold" style={{ color: '#9d174d' }}>{p.perUnitShipping ? fmtUsd(p.perUnitShipping) : '—'}</td>
                <td className="text-end">{p.totalQty}</td>
                <td className="text-end fw-bold">{fmtUsd(p.totalShipping)}</td>
              </tr>
            ))}
            <tr style={{ borderTop: '2px solid var(--border)', fontWeight: 700, background: '#f9fafb' }}>
              <td colSpan={6}>Total ({filtered.length} SKUs)</td>
              <td className="text-end">{filtered.reduce((s, p) => s + p.totalQty, 0)}</td>
              <td className="text-end">{fmtUsd(filtered.reduce((s, p) => s + p.totalShipping, 0))}</td>
            </tr>
          </tbody>
        </table></div>
      </div>
    </div>
  );
}

function LandedCosts() {
  // Per-SI manual cost entry in NIS. Each SI has its own usd_ils_rate so conversions
  // reflect the FX rate at the time of that specific shipment. 8 cost fields per SI:
  // 6 import + 2 export, all in NIS, plus a per-row USD/ILS rate.
  const { i18n } = useTranslation();
  const he = i18n.language === 'he';
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const r = await api.get('/settings/shipment-costs');
      setOrders(r.data.orders || []);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveField = async (orderNo, field, value) => {
    const v = value === '' ? 0 : (parseFloat(value) || 0);
    await api.put(`/settings/shipment-costs/${orderNo}`, { field, value: v });
    fetchAll();
  };
  // Convenience wrapper used by both cost cells and rate cell
  const saveCost = saveField;

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  // Field labels (Hebrew + English)
  const FIELDS = [
    { key: 'import_transport_nis',       he: 'הובלת מכולה למחסן', en: 'Container Transport',     side: 'import' },
    { key: 'import_unload_nis',          he: 'פריקת מכולה',        en: 'Container Unload',       side: 'import' },
    { key: 'import_pallet_receive_nis',  he: 'קליטת משטחים',       en: 'Pallet Receive',         side: 'import' },
    { key: 'import_storage_nis',         he: 'אחסנה',               en: 'Storage',                 side: 'import' },
    { key: 'import_insurance_nis',       he: 'ביטוח',               en: 'Insurance',               side: 'import' },
    { key: 'import_pallet_supplied_nis', he: 'משטח מסופק',         en: 'Pallet Supplied',        side: 'import' },
    { key: 'export_transport_nis',       he: 'הובלה לנמל',          en: 'Transport to Port',      side: 'export' },
    { key: 'export_loading_nis',         he: 'העמסת מכולה',        en: 'Container Loading',      side: 'export' },
  ];

  const fmtNis = (v) => `₪${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  const fmtUsd = (v) => `$${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // Aggregates
  const grand = orders.reduce((acc, o) => {
    for (const f of FIELDS) acc[f.key] += parseFloat(o.costs[f.key]) || 0;
    acc.totalNis += o.totalNis;
    acc.totalUsd += o.totalUsd;
    acc.totalUnits += o.totalUnits;
    return acc;
  }, { totalNis: 0, totalUsd: 0, totalUnits: 0, ...Object.fromEntries(FIELDS.map(f => [f.key, 0])) });

  return (
    <div>
      <h2 style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, letterSpacing: 1, marginBottom: 8 }}>
        {he ? 'עלויות נלוות פר משלוח' : 'Shipment Costs per SI'}
      </h2>
      <p className="text-muted" style={{ fontSize: 13, marginBottom: 16 }}>
        {he
          ? 'הקלד עלויות בשקלים ידנית לכל SI. שער הדולר נשמר פר SI כדי לשקף את התעריף בזמן המשלוח.'
          : 'Enter NIS amounts manually per SI. USD/ILS rate is stored per SI to reflect the rate at shipment time.'}
      </p>

      {/* SI cost table — fixed widths via colgroup so totals align under data cells */}
      <div className="table-wrap" style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse' }}>
          <colgroup>
            <col style={{ width: 120 }} />{/* SI */}
            <col style={{ width: 86 }} />{/* Arrival */}
            <col style={{ width: 64 }} />{/* Units */}
            <col style={{ width: 76 }} />{/* USD/ILS rate */}
            {FIELDS.map(f => <col key={f.key} style={{ width: 86 }} />)}
            <col style={{ width: 88 }} />{/* Total NIS */}
            <col style={{ width: 88 }} />{/* Total USD */}
            <col style={{ width: 78 }} />{/* $/Unit */}
          </colgroup>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              <th rowSpan={2} style={{ padding: '8px 6px' }}>SI</th>
              <th rowSpan={2} style={{ padding: '8px 6px' }}>{he ? 'הגעה' : 'Arrival'}</th>
              <th rowSpan={2} className="text-end" style={{ padding: '8px 6px' }}>{he ? 'יחידות' : 'Units'}</th>
              <th rowSpan={2} className="text-end" style={{ padding: '8px 6px', color: '#7c3aed' }}>{he ? 'שער $/₪' : 'USD/ILS'}</th>
              <th colSpan={6} style={{ background: '#eff6ff', borderBottom: '1px solid #bfdbfe', textAlign: 'center', padding: '6px' }}>
                {he ? '🚢 יבוא — ₪' : '🚢 Import — ₪'}
              </th>
              <th colSpan={2} style={{ background: '#fef3c7', borderBottom: '1px solid #fcd34d', textAlign: 'center', padding: '6px' }}>
                {he ? '📦 יצוא — ₪' : '📦 Export — ₪'}
              </th>
              <th rowSpan={2} className="text-end fw-bold" style={{ padding: '8px 6px' }}>{he ? 'סה״כ ₪' : 'Total ₪'}</th>
              <th rowSpan={2} className="text-end fw-bold" style={{ color: '#1565c0', padding: '8px 6px' }}>{he ? 'סה״כ $' : 'Total $'}</th>
              <th rowSpan={2} className="text-end fw-bold" style={{ color: '#1565c0', padding: '8px 6px' }}>{he ? '$/יחידה' : '$/Unit'}</th>
            </tr>
            <tr style={{ background: '#f9fafb' }}>
              {FIELDS.map(f => (
                <th key={f.key} className="text-end" style={{
                  fontSize: 10, fontWeight: 600, padding: '4px 6px', lineHeight: 1.2,
                  background: f.side === 'import' ? '#eff6ff' : '#fef3c7',
                }}>
                  {he ? f.he : f.en}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 && (
              <tr><td colSpan={4 + FIELDS.length + 3} className="text-center text-muted" style={{ padding: 32 }}>
                {he ? 'אין הזמנות SI' : 'No SI orders found'}
              </td></tr>
            )}
            {orders.map(o => (
              <tr key={o.orderNo}>
                <td className="fw-bold" style={{ padding: '6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {o.orderNo}
                  {!o.received && <div className="text-muted" style={{ fontSize: 10 }}>(לא נקלט)</div>}
                </td>
                <td style={{ fontSize: 12, color: '#6b7280', padding: '6px' }}>
                  {o.arrivalDate ? new Date(o.arrivalDate).toLocaleDateString(he ? 'he-IL' : 'en-US', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—'}
                  <span style={{ marginInlineStart: 4, fontSize: 11 }}>{o.transport === 'AIR' ? '✈️' : '🚢'}</span>
                </td>
                <td className="text-end" style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, padding: '6px' }}>
                  {o.totalUnits.toLocaleString()}
                </td>
                {/* Per-SI USD/ILS rate */}
                <td className="text-end" style={{ padding: 0, background: 'rgba(243,232,255,0.4)' }}>
                  <input className="inline-edit" type="number" step="0.0001" min="0.01"
                    style={{ width: '100%', boxSizing: 'border-box', padding: '6px', textAlign: 'right', fontSize: 12, fontFamily: "'DM Mono', monospace", color: '#7c3aed', background: 'transparent', border: 'none', fontWeight: 600 }}
                    defaultValue={o.usdIlsRate.toFixed(4)}
                    key={`${o.orderNo}-rate-${o.usdIlsRate}`}
                    onBlur={(e) => saveField(o.orderNo, 'usd_ils_rate', e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && e.target.blur()} />
                </td>
                {FIELDS.map(f => {
                  const v = parseFloat(o.costs[f.key]) || 0;
                  return (
                    <td key={f.key} className="text-end" style={{ padding: 0, background: f.side === 'import' ? 'rgba(239,246,255,0.4)' : 'rgba(254,243,199,0.4)' }}>
                      <input className="inline-edit" type="number" step="1" min="0"
                        style={{ width: '100%', boxSizing: 'border-box', padding: '6px', textAlign: 'right', fontSize: 12, fontFamily: "'DM Mono', monospace", color: v > 0 ? '#374151' : '#9ca3af', background: 'transparent', border: 'none' }}
                        defaultValue={v > 0 ? v : ''}
                        placeholder="0"
                        key={`${o.orderNo}-${f.key}-${v}`}
                        onBlur={(e) => saveCost(o.orderNo, f.key, e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && e.target.blur()} />
                    </td>
                  );
                })}
                <td className="text-end fw-bold" style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, padding: '6px' }}>
                  {fmtNis(o.totalNis)}
                </td>
                <td className="text-end fw-bold" style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, padding: '6px', color: '#1565c0' }}>
                  {fmtUsd(o.totalUsd)}
                </td>
                <td className="text-end fw-bold" style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, padding: '6px', color: '#1565c0' }}>
                  {fmtUsd(o.costPerUnitUsd)}
                </td>
              </tr>
            ))}
            {orders.length > 0 && (
              <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)', background: '#f9fafb' }}>
                <td style={{ padding: '8px 6px' }}>{he ? 'סה״כ' : 'Total'}</td>
                <td style={{ padding: '8px 6px', color: '#9ca3af' }}>—</td>
                <td className="text-end" style={{ padding: '8px 6px', fontFamily: "'DM Mono', monospace", fontSize: 12 }}>{grand.totalUnits.toLocaleString()}</td>
                <td className="text-end" style={{ padding: '8px 6px', color: '#9ca3af' }}>—</td>
                {FIELDS.map(f => (
                  <td key={f.key} className="text-end" style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, padding: '8px 6px' }}>
                    {fmtNis(grand[f.key])}
                  </td>
                ))}
                <td className="text-end" style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, padding: '8px 6px' }}>
                  {fmtNis(grand.totalNis)}
                </td>
                <td className="text-end" style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, padding: '8px 6px', color: '#1565c0' }}>
                  {fmtUsd(grand.totalUsd)}
                </td>
                <td className="text-end" style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, padding: '8px 6px', color: '#1565c0' }}>
                  {grand.totalUnits > 0 ? fmtUsd(grand.totalUsd / grand.totalUnits) : '—'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UploadCenter() {
  const { i18n } = useTranslation();
  const he = i18n.language === 'he';
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    const formData = new FormData();
    for (const f of files) formData.append('files', f);
    try {
      const res = await api.post('/data-requests/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadResult(res.data);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) { alert('Upload failed: ' + (err.response?.data?.error || err.message)); }
  };

  return (
    <div>
      <h2 style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, letterSpacing: 1, marginBottom: 16 }}>{he ? 'מרכז העלאות' : 'Upload Center'}</h2>
      <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>Drop invoices or packing lists. Files with SI numbers in the filename are auto-matched to orders.</p>
      <div className="card" style={{ padding: 20 }}>
        <div style={{ border: '2px dashed #cbd5e1', borderRadius: 12, padding: 32, textAlign: 'center', cursor: 'pointer', background: '#fafafa' }}
          onClick={() => fileInputRef.current?.click()}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>📁</div>
          <div style={{ fontSize: 15, color: '#64748b', fontWeight: 600 }}>Click to select files</div>
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>PDF, JPG, PNG (max 20MB per file)</div>
          <input ref={fileInputRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={handleUpload} />
        </div>
        {uploadResult && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>{uploadResult.uploaded} file(s) uploaded:</div>
            {uploadResult.files.map((f, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 0', fontSize: 13, borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ fontSize: 16 }}>{f.matched ? '✅' : '❌'}</span>
                <span style={{ fontWeight: 500 }}>{f.originalName}</span>
                <span className="text-muted">→</span>
                <span style={{ fontWeight: 600, color: f.matched ? '#16a34a' : '#dc2626' }}>{f.matched ? `${f.orderNo} (${f.fileType})` : 'No match found'}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function EmailRequests() {
  const [settings, setSettings] = useState({});
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const [settingsRes, requestsRes] = await Promise.all([
        api.get('/data-requests/settings'),
        api.get('/data-requests'),
      ]);
      setSettings(settingsRes.data);
      setRequests(requestsRes.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveSetting = async (key, value) => {
    await api.put('/data-requests/settings', { key, value });
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const resend = async (id) => {
    try {
      await api.put(`/data-requests/${id}/resend`);
      fetchAll();
      alert('Email resent!');
    } catch (err) { alert('Failed: ' + (err.response?.data?.error || err.message)); }
  };

  const deleteReq = async (id) => {
    if (!window.confirm('Delete this request?')) return;
    await api.delete(`/data-requests/${id}`);
    fetchAll();
  };

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div>
      <h2 style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, letterSpacing: 1, marginBottom: 16 }}>Email Settings</h2>

      <div className="card" style={{ padding: 16, marginBottom: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: '10px 16px', alignItems: 'center', fontSize: 14 }}>
          <label style={{ fontWeight: 600, color: '#64748b' }}>Send To:</label>
          <input className="input input-sm" defaultValue={settings.send_to || ''} style={{ maxWidth: 350 }}
            onBlur={e => saveSetting('send_to', e.target.value)} />
          <label style={{ fontWeight: 600, color: '#64748b' }}>CC:</label>
          <input className="input input-sm" defaultValue={settings.cc || ''} style={{ maxWidth: 500 }}
            onBlur={e => saveSetting('cc', e.target.value)} />
          <label style={{ fontWeight: 600, color: '#64748b' }}>Auto-send after:</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input className="input input-sm" type="number" min="1" defaultValue={settings.days_after_arrival || 7} style={{ width: 60 }}
              onBlur={e => saveSetting('days_after_arrival', e.target.value)} />
            <span style={{ color: '#64748b', fontSize: 13 }}>days after arrival</span>
          </div>
          <label style={{ fontWeight: 600, color: '#64748b' }}>Nudge every:</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input className="input input-sm" type="number" min="1" defaultValue={settings.nudge_interval_days || 1} style={{ width: 60 }}
              onBlur={e => saveSetting('nudge_interval_days', e.target.value)} />
            <span style={{ color: '#64748b', fontSize: 13 }}>day(s) until answered</span>
          </div>
        </div>
      </div>

      {requests.length > 0 && (<>
        <h3 style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--text-muted)', marginBottom: 8 }}>Sent Requests</h3>
        <div className="table-wrap">
          <table>
            <thead><tr>
              <th>Order</th><th>Status</th><th>Sent</th><th>Reminders</th><th>Answer</th><th>Actions</th>
            </tr></thead>
            <tbody>
              {requests.map(r => (
                <tr key={r.id}>
                  <td className="fw-bold">{r.reference}</td>
                  <td>{r.answeredAt ? <span className="badge" style={{ background: '#dcfce7', color: '#16a34a' }}>Answered</span> : <span className="badge" style={{ background: '#fff7ed', color: '#ea580c' }}>Pending</span>}</td>
                  <td className="text-muted" style={{ fontSize: 12 }}>{r.sentAt ? new Date(r.sentAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—'}</td>
                  <td className="text-muted" style={{ fontSize: 12 }}>{r.reminderCount || 0}</td>
                  <td style={{ fontFamily: "'DM Mono', monospace", fontWeight: r.answer ? 700 : 400, color: r.answer ? '#16a34a' : '#9ca3af' }}>
                    {r.answer ? `$${parseFloat(r.answer).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—'}
                  </td>
                  <td style={{ display: 'flex', gap: 4 }}>
                    <button className="btn btn-sm btn-outline" onClick={() => resend(r.id)}>Resend</button>
                    <button className="btn btn-sm btn-danger" onClick={() => deleteReq(r.id)} style={{ fontSize: 11 }}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>)}
    </div>
  );
}

// ════════════════ Expenses — handling rates by family + monthly warehouse fee ════════════════
function ExpensesTab({ he }) {
  const [rules, setRules] = useState([]);
  const [fees, setFees] = useState([]);
  const [summary, setSummary] = useState(null);
  const [ruleForm, setRuleForm] = useState({ label: '', matchTokens: '', rateUsd: '' });
  const [feeForm, setFeeForm] = useState({ month: '', amountUsd: '', notes: '' });

  const load = async () => {
    try {
      const [r, f, s] = await Promise.all([
        api.get('/extra-expenses/handling-rules'),
        api.get('/extra-expenses/warehouse-fees'),
        api.get('/extra-expenses/summary'),
      ]);
      setRules(r.data || []); setFees(f.data || []); setSummary(s.data || null);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const saveRule = async (id, patch) => { try { await api.put(`/extra-expenses/handling-rules/${id}`, patch); load(); } catch (e) { alert(e.response?.data?.error || 'Failed'); } };
  const addRule = async (e) => { e.preventDefault(); try { await api.post('/extra-expenses/handling-rules', ruleForm); setRuleForm({ label: '', matchTokens: '', rateUsd: '' }); load(); } catch (e2) { alert(e2.response?.data?.error || 'Failed'); } };
  const delRule = async (id) => { if (!window.confirm(he ? 'למחוק כלל?' : 'Delete rule?')) return; try { await api.delete(`/extra-expenses/handling-rules/${id}`); load(); } catch {} };
  const addFee = async (e) => { e.preventDefault(); try { await api.post('/extra-expenses/warehouse-fees', feeForm); setFeeForm({ month: '', amountUsd: '', notes: '' }); load(); } catch (e2) { alert(e2.response?.data?.error || 'Failed'); } };
  const delFee = async (id) => { if (!window.confirm(he ? 'למחוק דמי מחסן?' : 'Delete fee?')) return; try { await api.delete(`/extra-expenses/warehouse-fees/${id}`); load(); } catch {} };

  return (
    <div>
      <h2 style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, letterSpacing: 1 }}>{he ? 'הוצאות תפעול נוספות' : 'OPERATING EXPENSES'}</h2>
      <p className="text-muted" style={{ fontSize: 13, marginBottom: 16 }}>{he ? 'עלות טיפול ליחידה לפי משפחת מוצר (על כל יחידה בהזמנות סלקום) + דמי מחסן חודשיים. שניהם ב-USD ומקטינים את "הרווח הנוכחי".' : 'Per-unit handling cost by product family (on every unit in Cellcom orders) + a monthly warehouse fee. Both are in USD and reduce "Current Profit".'}</p>

      {summary && (
        <div className="card" style={{ padding: 16, marginBottom: 24, display: 'flex', gap: 36, flexWrap: 'wrap' }}>
          <div><div className="text-muted" style={{ fontSize: 11 }}>{he ? 'טיפול (השנה)' : 'Handling (this year)'}</div><div style={{ fontSize: 20, fontWeight: 700, color: '#7b1fa2' }}>${(summary.handling || 0).toFixed(2)}</div></div>
          <div><div className="text-muted" style={{ fontSize: 11 }}>{he ? 'מחסן (השנה)' : 'Warehouse (this year)'}</div><div style={{ fontSize: 20, fontWeight: 700, color: '#1565c0' }}>${(summary.warehouse || 0).toFixed(2)}</div></div>
          <div><div className="text-muted" style={{ fontSize: 11 }}>{he ? 'סה"כ הוצאה נוספת' : 'Total extra expense'}</div><div style={{ fontSize: 20, fontWeight: 700, color: '#c62828' }}>${(summary.total || 0).toFixed(2)}</div></div>
          <div><div className="text-muted" style={{ fontSize: 11 }}>{he ? 'יחידות במלאי' : 'On-hand units'}</div><div style={{ fontSize: 20, fontWeight: 700 }}>{summary.onHandUnits || 0}</div></div>
        </div>
      )}

      <h3 style={{ marginBottom: 8 }}>{he ? 'עלות טיפול לפי משפחה ($ ליחידה שנשלחה)' : 'Handling rate by family ($ / unit shipped)'}</h3>
      <div className="card" style={{ padding: 16, marginBottom: 12 }}>
        <form onSubmit={addRule} style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'end' }}>
          <div><label style={{ fontSize: 11, color: 'var(--text-dim)', display: 'block', marginBottom: 4 }}>{he ? 'שם' : 'Label'}</label><input className="input" value={ruleForm.label} onChange={e => setRuleForm(p => ({ ...p, label: e.target.value }))} required style={{ width: 160 }} /></div>
          <div><label style={{ fontSize: 11, color: 'var(--text-dim)', display: 'block', marginBottom: 4 }}>{he ? 'מילים לזיהוי (למשל "acton 3")' : 'Match tokens (e.g. "acton 3")'}</label><input className="input" value={ruleForm.matchTokens} onChange={e => setRuleForm(p => ({ ...p, matchTokens: e.target.value }))} required style={{ width: 220 }} /></div>
          <div><label style={{ fontSize: 11, color: 'var(--text-dim)', display: 'block', marginBottom: 4 }}>$ / {he ? 'יחידה' : 'unit'}</label><input className="input" type="number" step="0.01" min="0" value={ruleForm.rateUsd} onChange={e => setRuleForm(p => ({ ...p, rateUsd: e.target.value }))} required style={{ width: 90 }} /></div>
          <button type="submit" className="btn btn-gold">{he ? 'הוסף' : 'Add'}</button>
        </form>
      </div>
      {rules.length > 0 && (
        <div className="table-wrap"><table><thead><tr>
          <th>{he ? 'שם' : 'Label'}</th><th>{he ? 'זיהוי (מילים)' : 'Match tokens'}</th><th className="text-end">$ / {he ? 'יח׳' : 'unit'}</th><th></th>
        </tr></thead><tbody>
          {rules.map(r => (
            <tr key={r.id}>
              <td>{r.label}</td>
              <td><input className="inline-edit" defaultValue={r.match_tokens} onBlur={e => { if (e.target.value !== r.match_tokens) saveRule(r.id, { matchTokens: e.target.value }); }} style={{ width: 200 }} /></td>
              <td className="text-end"><input className="inline-edit" type="number" step="0.01" min="0" defaultValue={parseFloat(r.rate_usd)} onBlur={e => { const v = parseFloat(e.target.value); if (!isNaN(v) && v !== parseFloat(r.rate_usd)) saveRule(r.id, { rateUsd: v }); }} style={{ width: 80, textAlign: 'right' }} /></td>
              <td><button className="btn btn-sm btn-danger" onClick={() => delRule(r.id)}>{he ? 'מחק' : 'Delete'}</button></td>
            </tr>
          ))}
        </tbody></table></div>
      )}

      <h3 style={{ marginTop: 28, marginBottom: 4 }}>{he ? 'דמי מחסן חודשיים ($)' : 'Monthly warehouse fee ($)'}</h3>
      <p className="text-muted" style={{ fontSize: 12, marginBottom: 12 }}>{he ? 'הסכום החודשי מתחלק על כל היחידות שנשלחו + הקיימות במלאי באותו חודש.' : 'The monthly amount spreads per-unit across all units shipped + on-hand that month.'}</p>
      <div className="card" style={{ padding: 16, marginBottom: 12 }}>
        <form onSubmit={addFee} style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'end' }}>
          <div><label style={{ fontSize: 11, color: 'var(--text-dim)', display: 'block', marginBottom: 4 }}>{he ? 'חודש' : 'Month'}</label><input className="input" type="month" value={feeForm.month} onChange={e => setFeeForm(p => ({ ...p, month: e.target.value }))} required style={{ width: 160 }} /></div>
          <div><label style={{ fontSize: 11, color: 'var(--text-dim)', display: 'block', marginBottom: 4 }}>$ {he ? 'סכום' : 'Amount'}</label><input className="input" type="number" step="0.01" min="0" value={feeForm.amountUsd} onChange={e => setFeeForm(p => ({ ...p, amountUsd: e.target.value }))} required style={{ width: 120 }} /></div>
          <div><label style={{ fontSize: 11, color: 'var(--text-dim)', display: 'block', marginBottom: 4 }}>{he ? 'הערה' : 'Notes'}</label><input className="input" value={feeForm.notes} onChange={e => setFeeForm(p => ({ ...p, notes: e.target.value }))} style={{ width: 200 }} /></div>
          <button type="submit" className="btn btn-gold">{he ? 'שמור' : 'Save'}</button>
        </form>
      </div>
      {fees.length > 0 && (
        <div className="table-wrap"><table><thead><tr>
          <th>{he ? 'חודש' : 'Month'}</th><th className="text-end">$ {he ? 'סכום' : 'Amount'}</th><th>{he ? 'הערה' : 'Notes'}</th><th></th>
        </tr></thead><tbody>
          {fees.map(f => (
            <tr key={f.id}>
              <td>{f.month}</td>
              <td className="text-end">${parseFloat(f.amount_usd).toFixed(2)}</td>
              <td className="text-muted">{f.notes || ''}</td>
              <td><button className="btn btn-sm btn-danger" onClick={() => delFee(f.id)}>{he ? 'מחק' : 'Delete'}</button></td>
            </tr>
          ))}
        </tbody></table></div>
      )}
    </div>
  );
}
